import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, X } from 'lucide-react';

interface SuccessToastProps {
  message: string;
  /** Countdown duration in milliseconds. Defaults to five seconds. */
  durationMs?: number;
  onDismiss: () => void;
}

/** Mount for each notification; the parent owns visibility and the business action. */
export default function SuccessToast({ message, durationMs = 5000, onDismiss }: SuccessToastProps) {
  const dismissRef = useRef(onDismiss);
  useEffect(() => { dismissRef.current = onDismiss; }, [onDismiss]);
  useEffect(() => {
    const timeout = window.setTimeout(() => dismissRef.current(), durationMs);
    return () => window.clearTimeout(timeout);
  }, [durationMs]);

  return createPortal(
    <div className="fixed right-4 top-4 z-[60] flex h-[63px] w-[335px] max-w-[calc(100vw-2rem)] items-center gap-2 overflow-hidden rounded bg-white pl-[9px] pr-6 shadow-[0_1px_10px_rgba(0,0,0,0.13)]">
      <CheckCircle2 aria-hidden="true" className="h-[17px] w-[17px] shrink-0 fill-[#39b54a] text-white" />
      <p role="status" aria-live="polite" className="min-w-0 flex-1 text-[12px] leading-[18px] text-[#5e6572]">{message}</p>
      <button type="button" aria-label="Dismiss success notification" onClick={onDismiss} className="absolute right-0 top-0 flex h-6 w-6 items-start justify-end pr-[5px] pt-[6px] text-[#c2c9d1] hover:text-[#5e6572] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"><X aria-hidden="true" className="h-[9px] w-[9px]" /></button>
      <div key={durationMs} aria-hidden="true" className="success-toast-progress absolute inset-x-0 bottom-0 h-[6px] origin-left bg-[#39b54a]" style={{ animationDuration: `${durationMs}ms` }} />
    </div>, document.body,
  );
}
